import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NumberFormat from 'react-number-format';
import {
  Card,
  Grid,
  Typography,
  Divider,
  Switch,
  TextField,
  Button,
  Tooltip
} from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Breadcrumb, BackdropCircular } from 'components';
import { getStoreConfigsApi, updateStoreConfigsApi } from 'api';
import { changeStoreConfig } from 'redux/actions/store_configs';


const validationSchema = yup.object({
  isFlatRate: yup
    .boolean(),
  flatRatePrice: yup
    .number()
    .when('isFlatRate', {
      is: true,
      then: yup
        .number()
        .moreThan(0, 'Price should have only positive value')
        .required('Price is required')
    }),
  isFreeShip: yup
    .boolean(),
  freeShipAmount: yup
    .number()
    .when('isFreeShip', {
      is: true,
      then: yup
        .number()
        .moreThan(-1, 'Price should have 0 or positive value')
        .required('Amount is required')
    })
});

export default function ShippingConf() {
  const dispatch = useDispatch();

  const {storeConfigs, loading, error} = useSelector((state) => state.store_configs);

  useEffect(() => {
    if (storeConfigs.length === 0 || storeConfigs === undefined) {
      getStoreConfigsApi({dispatch});
    }
  }, []);

  const handleChangeConfigs = (event, pathName) => {
    const {checked, type, value} = event.target;
    dispatch(changeStoreConfig(pathName, checked, type, value));

    if (pathName == 'carriers/flatrate/price') {
      dispatch(changeStoreConfig('carriers/flatrate/title', checked, 'text', `$${value} Per Order`));
    } else if (pathName == 'carriers/freeshipping/free_shipping_subtotal') {
      const title = storeConfigs.find((config) => config.path === 'carriers/freeshipping/title').value.split(' ').map((i) => i.includes('$') ? `$${value}` : i).join(' ');
      dispatch(changeStoreConfig('carriers/freeshipping/title', checked, 'text', title));
    }
  };

  const handleSave = () => {
    const configs = storeConfigs.filter((config) => {
      return (config.path === 'carriers/flatrate/active') ||
             (config.path === 'carriers/flatrate/price') ||
             (config.path === 'carriers/flatrate/title') ||
             (config.path === 'carriers/freeshipping/active') ||
             (config.path === 'carriers/freeshipping/free_shipping_subtotal') ||
             (config.path === 'carriers/freeshipping/title');
    });
    updateStoreConfigsApi({dispatch, configs});
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <div>
        <Breadcrumb
          routeSegments={[
            { name: 'Settings', path: '/settings#go_live_requirements' },
            { name: 'Store Configurations', path: '/settings#store_configurations' },
            { name: 'Shipping' }
          ]}
        />
      </div>
      <div className="mt-10 mx-10">
        {!loading && error && <div className="m-10">Can&apos;t fetch Shipping data</div>}
        {!loading && storeConfigs.length !== 0 &&
        <Formik
          initialValues={{
            isFlatRate: storeConfigs.find((config) => config.path === 'carriers/flatrate/active').value == 1,
            flatRatePrice: storeConfigs.find((config) => config.path === 'carriers/flatrate/price').value  || '',
            isFreeShip: storeConfigs.find((config) => config.path === 'carriers/freeshipping/active').value == 1,
            freeShipAmount: storeConfigs.find((config) => config.path === 'carriers/freeshipping/free_shipping_subtotal').value || 0
          }}
          onSubmit={handleSave}
          enableReinitialize
          validationSchema={validationSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            setFieldValue
          }) => (
            <form onSubmit={handleSubmit}>
              <Card className="p-8">

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <p className="text-gray">In this section you can configure shipping rates for your store</p>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h4">Flat Rate</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    Enable Flat Rate:
                  </Grid>
                  <Grid item xs={6}>
                    <Switch
                      color="primary"
                      name="isFlatRate"
                      checked={values.isFlatRate}
                      onChange={(e) => handleChangeConfigs(e, 'carriers/flatrate/active')}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    Price:
                  </Grid>
                  <Grid item xs={6} className="flex items-center">
                    <Typography variant="body1" component="span" className="mr-2">$</Typography>
                    <Tooltip title={values.isFlatRate ? 'Enter Flat Rate Price' : 'Enable Flat Rate to have ability to change price'}>
                      <NumberFormat
                        variant="outlined"
                        label="Price"
                        allowNegative={false}
                        value={values.flatRatePrice}
                        disabled={storeConfigs.find((config) => config.path === 'carriers/flatrate/active').value != 1}
                        name="flatRatePrice"
                        onChange={(event) => {
                          setFieldValue('flatRatePrice', event.target.value);
                          handleChangeConfigs(event, 'carriers/flatrate/price');
                        }}
                        onBlur={(event) => {
                          handleBlur(event);
                          handleChangeConfigs(event, 'carriers/flatrate/price');
                        }}
                        error={touched.flatRatePrice && Boolean(errors.flatRatePrice)}
                        helperText={touched.flatRatePrice && errors.flatRatePrice}
                        customInput={TextField}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6}>
                    Title:
                  </Grid>
                  <Grid item xs={6} className="flex items-center">
                    <Typography variant="p">{storeConfigs.find((config) => config.path === 'carriers/flatrate/title').value}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h4">Free Shipping</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    Enable Free Shipping:
                  </Grid>
                  <Grid item xs={6}>
                    <Switch
                      color="primary"
                      name="isFreeShip"
                      checked={values.isFreeShip}
                      onChange={(e) => handleChangeConfigs(e, 'carriers/freeshipping/active')}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    Minimum Order Amount:
                  </Grid>
                  <Grid item xs={6} className="flex items-center">
                    <Typography variant="body1" component="span" className="mr-2">$</Typography>
                    <Tooltip title={values.isFreeShip ? 'Enter Free Ship Amount' : 'Enable Free Shipping to have ability to change the amount of free shipping'}>
                      <NumberFormat
                        variant="outlined"
                        label="Price"
                        allowNegative={false}
                        value={values.freeShipAmount}
                        disabled={storeConfigs.find((config) => config.path === 'carriers/freeshipping/active').value != 1}
                        name="freeShipAmount"
                        onChange={(event) => {
                          setFieldValue('freeShipAmount', event.target.value);
                          handleChangeConfigs(event, 'carriers/freeshipping/free_shipping_subtotal');
                        }}
                        onBlur={(event) => {
                          handleBlur(event);
                          handleChangeConfigs(event, 'carriers/freeshipping/free_shipping_subtotal');
                        }}
                        error={touched.freeShipAmount && Boolean(errors.freeShipAmount)}
                        helperText={touched.freeShipAmount && errors.freeShipAmount}
                        customInput={TextField}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6}>
                    Title:
                  </Grid>
                  <Grid item xs={6} className="flex items-center">
                    <Typography variant="p">{storeConfigs.find((config) => config.path === 'carriers/freeshipping/title').value}</Typography>
                  </Grid>
                </Grid>
              </Card>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="my-10"
                >
                  Save Configurations
                </Button>
              </div>
            </form>)}
        </Formik>
        }
      </div>
      <BackdropCircular loading={loading} />
    </div>
  );
}
